'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'
import { LMS, LMS_MODULES } from './data'

/* The bookend: the closing headline is the authored tagline, so the page ends
   on the product's own words. */

export function LmsCta() {
  const rail = useReveal<HTMLDivElement>(0.25)

  return (
    <section className="relative mt-16 overflow-hidden sm:mt-20 md:mt-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[70%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          <Eyebrow>Ready when you are</Eyebrow>

          <h2 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[1.1]">
            <ChromaText>{LMS.tagline}</ChromaText>
          </h2>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {LMS.description}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="/customer-stories">Read success stories</a>
            </Button>
          </div>
        </div>
      </div>

      <div ref={rail.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-3 lg:grid-cols-6">
          {LMS_MODULES.map((m, i) => (
            <div
              key={m.name}
              className="flex items-center gap-2.5 bg-background px-4 py-5 sm:px-5"
              style={{
                opacity: rail.shown ? 1 : 0,
                transition: `opacity 460ms ${EASE} ${i * 70}ms`,
              }}
            >
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
              />
              <span className="font-heading text-[13px] font-normal leading-tight text-foreground sm:text-sm">
                {m.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
